import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ReportTypeSelector from './components/ReportTypeSelector';
import AdvancedFilters from './components/AdvancedFilters';
import DataVisualization from './components/DataVisualization';
import ExportOptions from './components/ExportOptions';

const AdvancedReportBuilder = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isStaff, loading: authLoading } = useAuth();
  const [reportType, setReportType] = useState('sales');
  const [filters, setFilters] = useState({
    dateRange: '30',
    startDate: null,
    endDate: null,
    status: 'all',
    paymentMethod: 'all',
    memberId: null,
    category: 'all'
  });
  const [reportData, setReportData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);

  const isStaffMember = isGymOwner || isStaff;

  useEffect(() => {
    if (!authLoading && (!user || !isStaffMember)) {
      navigate('/member-dashboard');
    }
  }, [user, isStaffMember, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && isStaffMember) {
      generateReport();
    }
  }, [currentTenant, isStaffMember, reportType, filters]);

  const generateReport = async () => {
    setLoading(true);
    setError('');
    try {
      const { startDate, endDate } = getDateRange();

      switch (reportType) {
        case 'sales':
          await generateSalesReport(startDate, endDate);
          break;
        case 'attendance':
          await generateAttendanceReport(startDate, endDate);
          break;
        case 'revenue':
          await generateRevenueReport(startDate, endDate);
          break;
        case 'membership':
          await generateMembershipReport(startDate, endDate);
          break;
        case 'reconciliation':
          await generateReconciliationReport(startDate, endDate);
          break;
        default:
          break;
      }
    } catch (err) {
      setError(err?.message || 'Failed to generate report');
      console.error('Generate report error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = () => {
    if (filters?.startDate && filters?.endDate) {
      return { startDate: filters?.startDate, endDate: filters?.endDate };
    }
    const daysAgo = parseInt(filters?.dateRange || '30');
    const endDate = new Date();
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - daysAgo);
    return { startDate: startDate?.toISOString(), endDate: endDate?.toISOString() };
  };

  const generateSalesReport = async (startDate, endDate) => {
    let query = supabase
      ?.from('pos_transactions')
      ?.select(`
        *,
        pos_transaction_items(*, products(name, category))
      `)
      ?.eq('tenant_id', currentTenant?.id)
      ?.gte('transaction_date', startDate)
      ?.lte('transaction_date', endDate)
      ?.order('transaction_date', { ascending: false });

    if (filters?.status !== 'all') {
      query = query?.eq('payment_status', filters?.status);
    }
    if (filters?.paymentMethod !== 'all') {
      query = query?.eq('payment_method', filters?.paymentMethod);
    }

    const { data, error } = await query;
    if (error) throw error;

    setReportData(data || []);

    // Calculate stats
    const totalRevenue = data?.reduce((sum, t) => sum + parseFloat(t?.total_amount || 0), 0) || 0;
    const totalTransactions = data?.length || 0;
    const averageTransaction = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;

    setStats({
      totalRevenue,
      totalTransactions,
      averageTransaction,
      totalItems: data?.reduce((sum, t) => sum + (t?.pos_transaction_items?.length || 0), 0) || 0
    });

    // Process chart data
    const chartMap = {};
    data?.forEach(transaction => {
      const date = new Date(transaction?.transaction_date)?.toISOString()?.split('T')?.[0];
      if (!chartMap?.[date]) {
        chartMap[date] = { date, revenue: 0, transactions: 0 };
      }
      chartMap[date].revenue += parseFloat(transaction?.total_amount || 0);
      chartMap[date].transactions += 1;
    });
    setChartData(Object?.values(chartMap)?.sort((a, b) => new Date(a?.date) - new Date(b?.date)));
  };

  const generateAttendanceReport = async (startDate, endDate) => {
    const { data, error } = await supabase
      ?.from('attendance_logs')
      ?.select(`
        *,
        user_profiles!attendance_logs_user_id_fkey(id, full_name, email)
      `)
      ?.eq('tenant_id', currentTenant?.id)
      ?.gte('check_in_time', startDate)
      ?.lte('check_in_time', endDate)
      ?.order('check_in_time', { ascending: false });

    if (error) throw error;

    setReportData(data || []);

    const totalVisits = data?.length || 0;
    const uniqueMembers = new Set(data?.map(log => log?.user_id))?.size;
    const avgDuration = data?.filter(log => log?.duration_minutes)?.reduce((sum, log) => sum + (log?.duration_minutes || 0), 0) / (data?.filter(log => log?.duration_minutes)?.length || 1);

    setStats({
      totalVisits,
      uniqueMembers,
      averageDuration: Math?.round(avgDuration),
      peakDay: 'Monday'
    });

    // Process chart data
    const chartMap = {};
    data?.forEach(log => {
      const date = new Date(log?.check_in_time)?.toISOString()?.split('T')?.[0];
      if (!chartMap?.[date]) {
        chartMap[date] = { date, visits: 0, uniqueMembers: new Set() };
      }
      chartMap[date].visits += 1;
      chartMap?.[date]?.uniqueMembers?.add(log?.user_id);
    });
    setChartData(Object?.values(chartMap)?.map(day => ({
      date: day?.date,
      visits: day?.visits,
      uniqueMembers: day?.uniqueMembers?.size
    }))?.sort((a, b) => new Date(a?.date) - new Date(b?.date)));
  };

  const generateRevenueReport = async (startDate, endDate) => {
    const { data, error } = await supabase
      ?.from('billing_records')
      ?.select('*')
      ?.eq('tenant_id', currentTenant?.id)
      ?.gte('billing_date', startDate)
      ?.lte('billing_date', endDate)
      ?.order('billing_date', { ascending: false });

    if (error) throw error;

    setReportData(data || []);

    const totalRevenue = data?.reduce((sum, r) => sum + parseFloat(r?.total_amount || 0), 0) || 0;
    const completedRevenue = data?.filter(r => r?.payment_status === 'completed')?.reduce((sum, r) => sum + parseFloat(r?.total_amount || 0), 0) || 0;
    const pendingRevenue = data?.filter(r => r?.payment_status === 'pending')?.reduce((sum, r) => sum + parseFloat(r?.total_amount || 0), 0) || 0;

    setStats({
      totalRevenue,
      completedRevenue,
      pendingRevenue,
      totalRecords: data?.length || 0
    });

    // Process chart data
    const chartMap = {};
    data?.forEach(record => {
      const date = new Date(record?.billing_date)?.toISOString()?.split('T')?.[0];
      if (!chartMap?.[date]) {
        chartMap[date] = { date, revenue: 0, count: 0 };
      }
      chartMap[date].revenue += parseFloat(record?.total_amount || 0);
      chartMap[date].count += 1;
    });
    setChartData(Object?.values(chartMap)?.sort((a, b) => new Date(a?.date) - new Date(b?.date)));
  };

  const generateMembershipReport = async (startDate, endDate) => {
    const { data, error } = await supabase
      ?.from('member_subscriptions')
      ?.select(`
        *,
        user_profiles:member_id(id, full_name, email),
        membership_plans:plan_id(plan_name, price)
      `)
      ?.eq('tenant_id', currentTenant?.id)
      ?.gte('created_at', startDate)
      ?.lte('created_at', endDate)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    setReportData(data || []);

    const activeSubscriptions = data?.filter(s => s?.status === 'active')?.length || 0;
    const cancelledSubscriptions = data?.filter(s => s?.status === 'cancelled')?.length || 0;
    const totalMRR = data?.filter(s => s?.status === 'active')?.reduce((sum, s) => sum + parseFloat(s?.final_price || 0), 0) || 0;

    setStats({
      totalSubscriptions: data?.length || 0,
      activeSubscriptions,
      cancelledSubscriptions,
      monthlyRecurringRevenue: totalMRR
    });

    // Process chart data
    const chartMap = {};
    data?.forEach(sub => {
      const date = new Date(sub?.created_at)?.toISOString()?.split('T')?.[0];
      if (!chartMap?.[date]) {
        chartMap[date] = { date, newSubscriptions: 0, revenue: 0 };
      }
      chartMap[date].newSubscriptions += 1;
      chartMap[date].revenue += parseFloat(sub?.final_price || 0);
    });
    setChartData(Object?.values(chartMap)?.sort((a, b) => new Date(a?.date) - new Date(b?.date)));
  };

  const generateReconciliationReport = async (startDate, endDate) => {
    const { data, error } = await supabase
      ?.from('transaction_reconciliation')
      ?.select('*')
      ?.eq('tenant_id', currentTenant?.id)
      ?.gte('transaction_date', startDate)
      ?.lte('transaction_date', endDate)
      ?.order('transaction_date', { ascending: false });

    if (error) throw error;

    setReportData(data || []);

    const matched = data?.filter(r => r?.reconciliation_status === 'matched')?.length || 0;
    const unmatched = data?.filter(r => r?.reconciliation_status === 'unmatched')?.length || 0;
    const totalDiscrepancy = data?.reduce((sum, r) => sum + Math.abs(parseFloat(r?.amount_difference || 0)), 0) || 0;

    setStats({
      totalRecords: data?.length || 0,
      matched,
      unmatched,
      totalDiscrepancy
    });

    // Process chart data
    const chartMap = {};
    data?.forEach(record => {
      const date = new Date(record?.transaction_date)?.toISOString()?.split('T')?.[0];
      if (!chartMap?.[date]) {
        chartMap[date] = { date, matched: 0, unmatched: 0 };
      }
      if (record?.reconciliation_status === 'matched') chartMap[date].matched += 1;
      else chartMap[date].unmatched += 1;
    });
    setChartData(Object?.values(chartMap)?.sort((a, b) => new Date(a?.date) - new Date(b?.date)));
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Advanced Report Builder</h1>
          <p className="text-muted-foreground">Create custom reports with advanced filtering and data visualization</p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-red-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">
              <Icon name="X" size={20} />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <ReportTypeSelector
              reportType={reportType}
              onChange={setReportType}
            />
            <AdvancedFilters
              filters={filters}
              onChange={setFilters}
              reportType={reportType}
            />
            <Button
              onClick={() => setShowExportModal(true)}
              variant="outline"
              className="w-full"
            >
              <Icon name="Download" size={18} />
              Export Report
            </Button>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            <DataVisualization
              reportType={reportType}
              reportData={reportData}
              chartData={chartData}
              stats={stats}
              loading={loading}
            />
          </div>
        </div>
      </main>

      {/* Export Modal */}
      {showExportModal && (
        <ExportOptions
          reportType={reportType}
          reportData={reportData}
          filters={filters}
          onClose={() => setShowExportModal(false)}
        />
      )}

      <MobileBottomNav />
    </div>
  );
};

export default AdvancedReportBuilder;